import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.map.js";
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue";
const _hoisted_1 = {
  key: 0,
  class: "mb-4"
};
const _hoisted_2 = {
  key: 0,
  class: "mt-2 text-xs text-slate-400"
};
const _hoisted_3 = {
  key: 1,
  class: "empty-state"
};
const _hoisted_4 = {
  key: 2,
  class: "flex items-center justify-center py-8"
};
import { ref, computed, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useUserStore } from '@/stores/user';
import { meetingApi } from '@/api/meeting';
import FileUpload from '@/components/FileUpload.vue';
import FileAttachment from '@/components/FileAttachment.vue';
const MAX_COUNT = 10;

// 归属判断：删除 = 上传人本人或会议管理者（后端归属授权强制，此处只做 UI 显隐）

export default {
  __name: 'MaterialPanel',
  props: {
    meetingId: {
      type: [Number, String],
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    // 待召开 + 当前用户为会议组织者/主持人（由详情抽屉按归属计算传入）
    canManage: {
      type: Boolean,
      default: false
    }
  },
  setup(__props, {
    expose: __expose
  }) {
    const props = __props;

    // 与 FileUpload max-count 对齐，达到上限时隐藏上传区
    const userStore = useUserStore();
    const currentUserId = computed(() => userStore.userInfo?.userId);
    const canRemoveFile = file => {
      if (props.readonly) return false;
      if (props.canManage) return true;
      if (currentUserId.value == null) return false;
      return Number(file.uploaderId) === Number(currentUserId.value);
    };

    // 列表数据
    const loading = ref(false);
    const materials = ref([]);

    // 上传状态：FileUpload 完成项暂存于此，watch 到新增后逐项绑定
    const pendingUploads = ref([]);
    const uploading = ref(false);

    // 删除中项 id（置位期间 FileAttachment 禁用对应删除按钮）
    const removingId = ref(null);

    // DTO → FileAttachment 项映射
    // previewMode 'blob' 使 FileAttachment/FilePreviewModal 优先走 loader 按需换预签名 URL；
    // url 字段保留作为兜底（持久化 url 约 1 小时后过期，loader 存在时优先走 loader）
    const materialFiles = computed(() => materials.value.map(m => ({
      id: m.id,
      fileKey: m.ossKey,
      url: m.url,
      name: m.fileName,
      size: m.fileSize,
      contentType: m.contentType,
      uploaderId: m.uploaderId,
      previewMode: 'blob'
    })));

    // 预览/下载时按需换取物料预签名访问 URL（返回 URL 字符串，与 FileAttachment loader 契约一致）
    const loadMaterialFile = async file => {
      return meetingApi.getMaterialAccessUrl(props.meetingId, file.id);
    };

    // 拉取物料列表
    const fetchList = async () => {
      if (!props.meetingId) return;
      loading.value = true;
      try {
        materials.value = (await meetingApi.getMaterialList(props.meetingId)) || [];
      } catch (error) {
        ElMessage.error('获取物料列表失败');
        console.error('获取物料列表失败:', error);
      } finally {
        loading.value = false;
      }
    };
    watch(() => props.meetingId, fetchList, {
      immediate: true
    });

    // 绑定已上传文件为会议物料
    const bindUploaded = async file => {
      await meetingApi.bindMaterial(props.meetingId, {
        fileName: file.originalName || file.name,
        ossKey: file.fileKey,
        url: file.url,
        fileSize: file.fileSize || file.size,
        contentType: file.contentType
      });
    };

    // FileUpload 上传完成后逐项绑定；成功后清空 pendingUploads 并刷新列表
    watch(pendingUploads, async (newList, oldList) => {
      const prevLength = oldList?.length || 0;
      if (!newList || newList.length <= prevLength) return;
      const added = newList.slice(prevLength);
      let failed = false;
      for (const file of added) {
        try {
          await bindUploaded(file);
        } catch (error) {
          failed = true;
          console.error('物料绑定失败:', error);
        }
      }
      if (failed) {
        ElMessage.error('物料绑定失败');
      }
      pendingUploads.value = [];
      fetchList();
    });

    // 删除物料（confirm 取消时静默忽略）
    const handleRemove = async file => {
      try {
        await ElMessageBox.confirm(`确定要删除物料「${file.name}」吗？`, '提示', {
          type: 'warning'
        });
      } catch {
        return;
      }
      removingId.value = file.id;
      try {
        await meetingApi.deleteMaterial(props.meetingId, file.id);
        ElMessage.success('物料已删除');
        fetchList();
      } catch (error) {
        ElMessage.error('删除物料失败');
        console.error('删除物料失败:', error);
      } finally {
        removingId.value = null;
      }
    };
    __expose({
      fetchList
    });
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("div", null, [!__props.readonly && materials.value.length < MAX_COUNT ? (_openBlock(), _createElementBlock("div", _hoisted_1, [_createVNode(FileUpload, {
        files: pendingUploads.value,
        "onUpdate:files": _cache[0] || (_cache[0] = $event => pendingUploads.value = $event),
        module: "meeting",
        bucket: "private",
        accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.zip,.rar",
        "max-count": MAX_COUNT,
        "max-size": 100,
        onUploadingChange: _cache[1] || (_cache[1] = $event => uploading.value = $event)
      }, null, 8, ["files"]), uploading.value ? (_openBlock(), _createElementBlock("p", _hoisted_2, "文件上传中，上传完成后将自动绑定为会议物料")) : _createCommentVNode("", true)])) : _createCommentVNode("", true), _createVNode(FileAttachment, {
        files: materialFiles.value,
        loader: loadMaterialFile,
        removable: canRemoveFile,
        "removing-id": removingId.value,
        onRemove: handleRemove
      }, null, 8, ["files", "removing-id"]), materials.value.length === 0 && !loading.value ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[2] || (_cache[2] = [_createElementVNode("p", {
        class: "empty-state-title"
      }, "暂无物料", -1)]))])) : _createCommentVNode("", true), loading.value ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[3] || (_cache[3] = [_createElementVNode("svg", {
        class: "w-8 h-8 animate-spin text-primary-500",
        fill: "none",
        viewBox: "0 0 24 24"
      }, [_createElementVNode("circle", {
        class: "opacity-25",
        cx: "12",
        cy: "12",
        r: "10",
        stroke: "currentColor",
        "stroke-width": "4"
      }), _createElementVNode("path", {
        class: "opacity-75",
        fill: "currentColor",
        d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      })], -1)]))])) : _createCommentVNode("", true)]);
    };
  }
};